import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import ProfileCard from "../components/ProfileCard";
import FiltersModal, { defaultFilters } from "../components/FiltersModal";

export default function ProfilesList() {
  const { type } = useParams(); // "canguro" or "profesor"
  const isProf = type === "profesor";
  const accent = isProf ? "teal" : "blue";
  const titleColor = isProf ? "text-teal-500" : "text-blue-600";

  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(defaultFilters());
  const [showFilters, setShowFilters] = useState(false);
  const [favIds, setFavIds] = useState(new Set());

  const fetchProfiles = useCallback(async (f = filters) => {
    setLoading(true);
    try {
      const params = { type, only_approved: true };
      if (f.comunidad) params.comunidad = f.comunidad;
      if (f.ciudad) params.ciudad = f.ciudad;
      if (f.zonas && f.zonas.length) params.zona = f.zonas[0]; // primary zona for backend, others client-side
      if (f.max_price < 50) params.max_price = f.max_price;
      if (f.availability && f.availability.length) params.availability = f.availability.join(",");
      if (f.languages && f.languages.length) params.language = f.languages.join(",");
      if (isProf && f.subjects) {
        const flat = Object.values(f.subjects).flat();
        if (flat.length) params.subject = flat.join(",");
      }
      const { data } = await api.get("/profiles", { params });
      // Client-side zone multi-filter
      let result = data;
      if (f.zonas && f.zonas.length > 1) {
        result = data.filter((p) => (p.zonas || []).some((z) => f.zonas.includes(z)));
      }
      setProfiles(result);
    } catch (e) {
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  }, [type, isProf, filters]);

  useEffect(() => { fetchProfiles(filters); /* eslint-disable-next-line */ }, [type]);

  useEffect(() => {
    api.get("/favorites").then(({ data }) => {
      setFavIds(new Set(data.map((p) => p.id)));
    }).catch(() => {});
  }, []);

  const toggleFavorite = async (profileId) => {
    if (favIds.has(profileId)) {
      await api.delete(`/favorites/${profileId}`);
      const ns = new Set(favIds); ns.delete(profileId); setFavIds(ns);
    } else {
      await api.post("/favorites", { profile_id: profileId });
      const ns = new Set(favIds); ns.add(profileId); setFavIds(ns);
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <Link to="/home" className="w-10 h-10 flex items-center justify-center rounded-full hover:bg-slate-100 text-slate-700" data-testid="back-to-home-btn">
          <i className="fa-solid fa-arrow-left text-lg"></i>
        </Link>
        <h1 className={`text-2xl md:text-3xl font-extrabold ${titleColor}`} data-testid="profiles-list-title">
          {isProf ? "Profesores" : "Canguros"}
        </h1>
        <button onClick={() => setShowFilters(true)} data-testid="open-filters-btn"
          className="w-10 h-10 flex items-center justify-center rounded-full border border-slate-200 hover:bg-slate-100">
          <i className="fa-solid fa-sliders"></i>
        </button>
      </div>

      <p className="font-bold text-slate-900">
        {isProf ? "Profesores" : "Canguros"} disponibles cerca de ti
      </p>

      {loading ? (
        <div className="text-center py-16 text-slate-500" data-testid="loading-state">Cargando…</div>
      ) : profiles.length === 0 ? (
        <div className="text-center py-16 text-slate-500" data-testid="empty-state">
          <i className="fa-solid fa-magnifying-glass text-3xl mb-3 text-slate-300"></i>
          <p className="font-semibold">Sin resultados</p>
          <p className="text-sm">Prueba a cambiar los filtros</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {profiles.map((p) => (
            <ProfileCard
              key={p.id}
              profile={p}
              accent={accent}
              isFavorite={favIds.has(p.id)}
              onToggleFavorite={toggleFavorite}
            />
          ))}
        </div>
      )}

      <FiltersModal
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onApply={(f) => { setFilters(f); fetchProfiles(f); }}
        initialFilters={filters}
        accent={accent}
        showSubjects={isProf}
      />
    </div>
  );
}
